import React, { FC, useCallback, useMemo } from 'react'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { styled } from '~/app/components'
import getCancellationOrderConverter from '~/app/components/order-converter/getCancellationOrderConverter'
import { notEmpty } from '~/app/utils'
import CancellationCardItem from '~/app/components/order-list/details/CancelInformation/CancellationCardItem'
import SubHeader from '~/app/components/common/SubHeader/SubHeader'
import { CategoryType } from '~/__generated__/__types__'
import { toCancellationLeisureOrder } from '~/app/components/order-converter/converter/LeisureOrderConverter'
import { getNowSeoulDateTime } from '~/app/utils/datetime'
import { useServerCurrentAtQuery } from '~/__generated__/app/query/server-current-at.graphql'

/**
 * CancellationCategoryOrders component
 * author: soojin.oh
 */

// type definitions
type Props = {
  categoryOrders?: OrderDetailsCancellationCategoryOrders
}

// styled components
const Container = styled.div`
  background: ${({ theme }) => theme.color.white};
  flex: 1 0 auto;
  padding-bottom: 6.4rem;
`

const useCancellationOrder = (orders?: OrderDetailsCancellationCategoryOrders) => {
  const { toCancellationDomesticOrder, toCancellationTrainOrder, toCancellationBusOrder } =
    getCancellationOrderConverter()
  const { data: { serverCurrentAt: currentAt } = { serverCurrentAt: getNowSeoulDateTime() } } =
    useServerCurrentAtQuery()
  const convert = useCallback(
    <T, U>(
      items: Array<T>,
      categoryOrderId: string,
      converter: (arg: T, categoryOrderId: string, categoryType?: CategoryType) => U
    ) => items.map(item => converter(item, categoryOrderId)),
    []
  )

  const cancellationOrders = useMemo(
    () =>
      orders
        ?.map(order =>
          order.__typename === 'OrderDetailsCancellationDomestic'
            ? convert([order], order.id, toCancellationDomesticOrder)
            : order.__typename === 'OrderDetailsCancellationTrain'
              ? convert(order.tickets, order.id, toCancellationTrainOrder)
              : order.__typename === 'OrderDetailsCancellationLeisureProduct'
                ? toCancellationLeisureOrder({
                    items: order.items,
                    categoryOrderId: order.id,
                    categoryType: order.category,
                    currentAt,
                  })
                : order.__typename === 'OrderDetailsCancellationBus'
                  ? toCancellationBusOrder(order.reservations, order.id)
                  : null
        )
        .filter(notEmpty)
        .flat(1),
    [orders]
  )

  return { cancellationOrders }
}

// CancellationCategoryOrders component
const CancellationCategoryOrderList: FC<Props> = function ({ categoryOrders }) {
  const { cancellationOrders } = useCancellationOrder(categoryOrders)
  return (
    <Container>
      <SubHeader title="취소 상품 정보" hasBottomContent />
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {cancellationOrders?.map(
          (order, index) =>
            // toCancellationTrainOrder 의 리턴 타입이 잘못 추론되면서 order 가 빈 오브젝트 {} 로 추론되어 빌드 오류가 발생한다
            // toCancellationTrainOrder 의 리턴 타입을 ReturnType<> 으로 추론해 보면 정상적으로 나오는데 이유를 모르겠음
            order && <CancellationCardItem key={index} order={order} />
        )}
      </div>
    </Container>
  )
}

export default CancellationCategoryOrderList
